// My programmes, as Account.dc.html draws it: Programme, What you have and How long it lasts, a row
// per product, then the link to the full terms. The rows come from account/programmes.js. A secured
// place says its balance's amount and due date, with the place secured page's pay button as a link
// under it (member review, 11 Oct; no board draws it).
//
// A product with a certificate has it under its row (stage 4 review; no board draws it): the
// certificate's number, opening the certificate page, and the certificate page's own 2 buttons,
// Download the PDF (certificate-pdf) and Copy the check link, which carries its token. While staff
// view as a member, the PDF isn't fetched (the function refuses it too).
import { Fragment, useState } from 'react'
import { SITE } from '../config/site.js'
import { Link } from '../lib/router.jsx'
import { copyText } from '../lib/clipboard.js'
import { callHubFunction, HubFunctionError } from '../lib/functions.js'
import { VIEWING_AS_LINE, readOnlyControl } from '../lib/viewAs.jsx'
import { COPY } from './copy.js'

const FALLBACK = 'Something went wrong. Try again.'

function CertificateRow({ c, readOnly }) {
  const [pdf, setPdf] = useState({ status: 'idle', line: null })
  const [copied, setCopied] = useState({ done: false, line: null })
  const checkUrl = `${window.location.origin}${c.checkPath}`

  async function download() {
    if (pdf.status === 'busy') return
    if (readOnly) { setPdf({ status: 'failed', line: VIEWING_AS_LINE }); return }
    setPdf({ status: 'busy', line: null })
    try {
      const blob = await callHubFunction('certificate-pdf', { certificate_id: c.id })
      if (!(blob instanceof Blob)) throw new Error('no pdf')
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `${c.courseName} certificate ${c.number}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(href), 1000)
      setPdf({ status: 'done', line: null })
    } catch (e) {
      setPdf({ status: 'failed', line: e instanceof HubFunctionError ? e.message : FALLBACK })
    }
  }

  async function copy() {
    const ok = await copyText(checkUrl)
    setCopied(ok ? { done: true, line: null } : { done: false, line: COPY.copyFailed(`${window.location.host}${c.checkPath}`) })
  }

  const line = pdf.line || copied.line
  return (
    <div className="ac-cert" role="row">
      <span className="ac-cert-cell" role="cell">
        <span className="ac-cert-what">
          <span className="ac-cert-label">{COPY.certificateLabel}</span>
          <Link to={c.href} className="ac-cert-link">{COPY.certificate(c.number)}</Link>
        </span>
        <span className="ac-cert-actions">
          <button type="button" className="ac-btn" onClick={download} aria-busy={pdf.status === 'busy' || undefined}>
            {pdf.status === 'done' ? COPY.pdfDownloaded : COPY.downloadPdf}
          </button>
          <button type="button" className="ac-btn" onClick={copy}>
            {copied.done ? COPY.checkCopied : COPY.copyCheck}
          </button>
        </span>
        {line && <span className="ac-cert-msg" role="alert">{line}</span>}
      </span>
    </div>
  )
}

export default function Programmes({ rows, readOnly = false }) {
  return (
    <section id="programmes" className="ac-card ac-card--programmes" aria-labelledby="programmes-h">
      <h2 id="programmes-h" className="ac-h2">{COPY.programmes}</h2>
      {rows === null && <div className="ac-table-loading" aria-busy="true" />}
      {rows && rows.length === 0 && <p className="ac-lede">{COPY.noProgrammes}</p>}
      {rows && rows.length > 0 && (
        <div className="ac-table" role="table" aria-labelledby="programmes-h">
          <div className="ac-thead" role="row">
            <span role="columnheader">{COPY.colProgramme}</span>
            <span role="columnheader">{COPY.colWhat}</span>
            <span role="columnheader">{COPY.colTerms}</span>
          </div>
          {rows.map(r => (
            <Fragment key={r.id}>
              <div className={r.certificate ? 'ac-trow ac-trow--with-cert' : 'ac-trow'} role="row">
                <span className="ac-prog-name" role="cell">
                  <span className={`ac-dot ac-dot--${r.dot}`} aria-hidden="true" />{r.name}
                </span>
                <span className="ac-prog-what" role="cell">
                  {r.what}
                  {r.status && <span className="ac-prog-status">{r.status}</span>}
                  {/* a secured place's balance, with the place secured page's own pay button (member
                      review, 11 Oct); while staff view as a member it's shown but doesn't open */}
                  {r.pay && (readOnly
                    ? <button type="button" className="ac-prog-pay" {...readOnlyControl(true)}>{r.pay.label}</button>
                    : <a className="ac-prog-pay" href={r.pay.href}>{r.pay.label}</a>)}
                </span>
                <span className="ac-prog-terms" role="cell">{r.terms}</span>
              </div>
              {r.certificate && <CertificateRow c={r.certificate} readOnly={readOnly} />}
            </Fragment>
          ))}
        </div>
      )}
      <a href={SITE.termsUrl} className="ac-terms-link">{COPY.readTerms}</a>
    </section>
  )
}
